import { useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { BrandWordmark } from "@/components/Brand";
import {
  LayoutDashboard, TrendingUp, Wallet, ShieldAlert, Trophy, Calculator,
  FileStack, PlusCircle, Database, AlertTriangle, CheckSquare, Receipt,
  History, LogOut, ChevronLeft, ChevronRight, Menu, BadgeCheck, CircleUserRound,
  GitBranch, UploadCloud, FileBarChart2,
} from "lucide-react";

const NAV = [
  { section: "Analytics", items: [
    { to: "/", label: "Executive", icon: LayoutDashboard },
    { to: "/sales", label: "Sales", icon: TrendingUp },
    { to: "/finance", label: "Finance", icon: Wallet },
    { to: "/data-quality", label: "Data Quality", icon: ShieldAlert },
    { to: "/milestone", label: "Milestone", icon: Trophy },
    { to: "/reports", label: "Reports", icon: FileBarChart2 },
  ]},
  { section: "Operations", items: [
    { to: "/calc-centre", label: "Calculation Centre", icon: Calculator },
    { to: "/schemes", label: "Schemes", icon: FileStack },
    { to: "/schemes/new", label: "Scheme Builder", icon: PlusCircle },
    { to: "/exceptions", label: "Exceptions", icon: AlertTriangle },
    { to: "/workflows", label: "Workflow Builder", icon: GitBranch },
    { to: "/upload-studio", label: "Upload Studio", icon: UploadCloud },
    { to: "/approvals", label: "Approvals", icon: CheckSquare },
    { to: "/settlements", label: "Settlements", icon: Receipt },
  ]},
  { section: "Governance", items: [
    { to: "/master-data", label: "Master Data", icon: Database },
    { to: "/audit", label: "Audit Explorer", icon: History },
    { to: "/acceptance-tests", label: "Acceptance Tests", icon: BadgeCheck },
  ]},
];

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleLogout = async () => { await logout(); nav("/login"); };

  const Sidebar = (
    <aside
      className={`adani-sidebar flex flex-col ${collapsed ? "w-16" : "w-64"} transition-all duration-300 shrink-0 h-screen sticky top-0`}
      data-testid="app-sidebar"
    >
      <div className="h-16 flex items-center px-4 border-b border-white/10">
        {collapsed ? <BrandWordmark light compact /> : <BrandWordmark light />}
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-2 space-y-5">
        {NAV.map((grp) => (
          <div key={grp.section}>
            {!collapsed && (
              <div className="px-3 mb-1.5 text-[10px] uppercase tracking-widest text-cyan-200/50 font-semibold">
                {grp.section}
              </div>
            )}
            <div className="space-y-1">
              {grp.items.map((it) => {
                const active = loc.pathname === it.to;
                const Icon = it.icon;
                return (
                  <button
                    key={it.to}
                    data-testid={`nav-${it.label.toLowerCase().replace(/\s+/g, "-")}`}
                    onClick={() => { nav(it.to); setMobileOpen(false); }}
                    className={`nav-item w-full flex items-center gap-3 px-3 py-2 rounded-lg text-sm font-medium border ${
                      active
                        ? "bg-white/10 text-white border-l-2 border-l-[#F59E0B] border-transparent"
                        : "text-slate-300 hover:bg-white/5 hover:text-white border-transparent"
                    }`}
                    title={it.label}
                  >
                    <Icon size={18} className="shrink-0" />
                    {!collapsed && <span className="truncate">{it.label}</span>}
                  </button>
                );
              })}
            </div>
          </div>
        ))}
      </nav>
      <button
        onClick={() => setCollapsed((c) => !c)}
        className="hidden lg:flex items-center justify-center h-10 border-t border-white/10 text-slate-400 hover:text-white"
        data-testid="sidebar-collapse-btn"
      >
        {collapsed ? <ChevronRight size={18} /> : <ChevronLeft size={18} />}
      </button>
    </aside>
  );

  return (
    <div className="flex min-h-screen">
      <div className="hidden lg:block">{Sidebar}</div>
      {mobileOpen && (
        <div className="lg:hidden fixed inset-0 z-50 flex">
          <div className="absolute inset-0 bg-black/50" onClick={() => setMobileOpen(false)} />
          <div className="relative z-10">{Sidebar}</div>
        </div>
      )}

      <div className="flex-1 min-w-0 flex flex-col">
        <header className="h-16 sticky top-0 z-40 backdrop-blur-md bg-white/85 border-b border-slate-200 flex items-center justify-between px-4 sm:px-6">
          <div className="flex items-center gap-3">
            <button className="lg:hidden text-slate-600" onClick={() => setMobileOpen(true)} data-testid="mobile-menu-btn">
              <Menu size={22} />
            </button>
            <div>
              <div className="font-heading font-bold text-slate-900 text-sm sm:text-base leading-tight">
                Trade Scheme Management &amp; Analytics
              </div>
              <div className="text-[11px] text-slate-400 font-medium">ACC &amp; ACL · Enterprise DIY Incentive Platform</div>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <div className="hidden sm:flex flex-col items-end leading-tight">
              <span className="text-sm font-semibold text-slate-800" data-testid="header-user-name">{user?.name}</span>
              <span className="text-[11px] text-[#00529B] font-medium">{user?.role}</span>
            </div>
            <div className="w-9 h-9 rounded-full adani-navy-gradient flex items-center justify-center text-white">
              <CircleUserRound size={20} />
            </div>
            <button
              onClick={handleLogout}
              data-testid="logout-btn"
              className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-[#DC2626] font-medium px-2 py-1.5 rounded-md hover:bg-red-50"
            >
              <LogOut size={16} /> <span className="hidden sm:inline">Logout</span>
            </button>
          </div>
        </header>
        <main className="flex-1 max-w-[1600px] w-full mx-auto p-4 sm:p-6 lg:p-8">{children}</main>
        <footer className="text-center text-[11px] text-slate-400 py-4 border-t border-slate-200">
          SchemeCentral · Enterprise DIY Scheme Digitization &amp; Incentive Analytics · Adani Cement (ACC + ACL)
        </footer>
      </div>
    </div>
  );
}
